/**
 * Batch = the same settings applied to every file (R1/R2). The master state is the one the user
 * edits on the selected photo; each other item receives a translation of it that respects its own
 * pixel size. With the ordered-ops model (2026-09-06) that is almost free: resize ops carry an
 * INTENT and derive their pixels from each file's own input; crops are fractions of their input;
 * rotation and flips copy verbatim. The one policy left is what a crop means on a photo of a
 * different size: the same region (fractions, only meaningful on a same-size photo) or the same
 * SHAPE centred (default).
 */
import { EditState, Size, Op, isIdentity, initialState, sizeAfter, cropRectPx, centredRectWithAspect, stepOutput } from './edit-stack';
import { OwnCrop, withOwnCrop, ownCropFromView, fullOwnCrop, workSize } from './own-crop';
export type { OwnCrop } from './own-crop';
export { hasOwnCrop } from './own-crop';

export const BATCH_LIMIT = 50;

export type CropPolicy = 'same-region' | 'centre-aspect';

export function applyMasterToItem(master: EditState, itemSource: Size, cropPolicy: CropPolicy = 'centre-aspect'): EditState {
    const s = initialState(itemSource);
    const sameSize = itemSource.width === master.source.width && itemSource.height === master.source.height;
    const blind = isUnsized(master.source);   // drawn on a photo the browser could not open: no pixel boxes known
    const ops: Op[] = [];
    let itemInput: Size = { ...s.source }; let k = 1;   // this file's own size and cumulative scale at each step
    master.ops.forEach((op, i) => {
        let o: Op = op;
        if (op.type === 'crop' && isIdentity(op) && !op.aspect) {
            // a FREE full-frame crop stays the full frame on every photo (never the owner's shape). A full frame
            // with a chosen aspect is that aspect drawn on a photo already in that shape (16:9 on a 1600 x 900):
            // it still crops the others (QA round 4 OC4-1)
        } else if (op.type === 'crop' && blind) {
            // only the chosen aspect is meaningful; a free crop keeps its fractions
            if (op.aspect && cropPolicy === 'centre-aspect') {
                const c = centredRectWithAspect(itemInput, op.aspect);
                o = { ...op, f: { x: c.x / itemInput.width, y: c.y / itemInput.height, w: c.width / itemInput.width, h: c.height / itemInput.height } };
            }
        } else if (op.type === 'crop' && !(cropPolicy === 'same-region' && sameSize)) {
            // the same shape (pixel aspect of the master's box at this step), centred on this photo's input
            const r = cropRectPx(op, sizeAfter(master, i));
            const c = centredRectWithAspect(itemInput, r.width / Math.max(1, r.height));
            o = { ...op, f: { x: c.x / itemInput.width, y: c.y / itemInput.height, w: c.width / itemInput.width, h: c.height / itemInput.height } };
        }
        ops.push(o);
        const r = stepOutput(o, itemInput, k); itemInput = r.out; k = r.k;
    });
    return { ...s, ops };
}

/** How far one file is, 0..1, across its phases: a server file reports the upload and the edge job each
 *  as their own 0..1, so the raw fraction falls back to 0 when processing begins (the ETA then climbed).
 *  The upload carries most of the time on a slow link. No phase (a browser file) = its fraction.
 *  F2 (usage QA 2026-10-07): 'processing-done' = the server finished, the download has not started (0.97); an
 *  upload at fraction 1 is the END of the upload, not 0 (J2-10: the bar went back to "Starting…"). */
type EtaActive = { startedAt: number; fraction: number; phase?: string };
const PHASE_SPAN: Record<string, [number, number]> = { uploading: [0, 0.8], processing: [0.8, 0.97], 'processing-done': [0.97, 0.97], downloading: [0.97, 1] };
function fileProgress(a: EtaActive): number {
    const span = a.phase ? PHASE_SPAN[a.phase] : undefined;
    if (span && a.phase === 'processing-done') return span[0];
    if (!Number.isFinite(a.fraction) || a.fraction < 0 || a.fraction > 1 || (!span && a.fraction >= 1)) return 0;
    return span ? span[0] + (span[1] - span[0]) * a.fraction : a.fraction;
}
/** What the bar showed last (one batch at a time). `secs` null = the number was withdrawn: it never comes back
 *  for this batch, so the user never sees it go up (J15-5, ruling: the ETA only goes down). */
let etaShown: { start: number; total: number; secs: number | null; at: number } | null = null;
export function resetEtaMemo(): void { etaShown = null; }

/** Below this the number is withdrawn while the work still runs: "about 1 s left" for 70 s was J16-8. */
const ETA_FLOOR_S = 2.5;

type EtaState = { kind: 'starting' | 'running' | 'done'; secs: number | null; raw: number | null };
function etaCompute(done: number, total: number, elapsedMs: number, active: EtaActive[] | undefined, now: number): EtaState {
    if (done >= total) return { kind: 'done', secs: null, raw: null };
    const start = now - elapsedMs;
    const same = !!etaShown && etaShown.total === total && Math.abs(etaShown.start - start) < 1000 && now >= etaShown.at;
    if (!same) etaShown = null;
    const gs = (active || []).map(a => ({ a, g: fileProgress(a), el: Math.min(Math.max(0, now - a.startedAt), elapsedMs) }));
    const partial = gs.reduce((sum, x) => sum + x.g, 0);
    const eff = Math.min(done + partial, total);
    if (eff <= 0 || eff >= total) return { kind: etaShown ? 'running' : 'starting', secs: null, raw: null };
    // time left = the larger of the average per finished file and each in-flight file's own pace (PLAN C3)
    let raw = (elapsedMs / eff) * (total - eff) / 1000;
    let unknown = false;
    for (const { a, g, el } of gs) {
        if (g >= 0.05 && el >= 1000) raw = Math.max(raw, el * (1 - g) / g / 1000);
        else if (a.phase) unknown = true;   // a server file that has not shown its pace yet
    }
    if (!etaShown) {
        // the first number waits for evidence (J2-10 "about 1 s left" 2 s into a 40 s upload; J3-6 "64 s" for 6
        // minutes): a finished file, or for one file 10 % of it after 3 s; and no server file of unknown pace
        const single = total === 1 && partial >= 0.1 && elapsedMs >= 3000;
        const allBrowser = gs.length > 0 && gs.every(x => !x.a.phase) && partial >= 0.05;
        if (unknown || !(done >= 1 || single || allBrowser) || raw < ETA_FLOOR_S + 1) return { kind: done > 0 || partial > 0 ? 'running' : 'starting', secs: null, raw };
        etaShown = { start, total, secs: raw, at: now };
        return { kind: 'running', secs: raw, raw };
    }
    if (etaShown.secs == null) { etaShown.at = now; return { kind: 'running', secs: null, raw }; }
    const countdown = etaShown.secs - (now - etaShown.at) / 1000;
    // down only: the lower of the countdown and the new estimate; withdrawn once it is clearly wrong (the work
    // needs more than twice what it says) or reaches the floor while work remains
    let secs: number | null = Math.min(countdown, raw);
    if (secs < ETA_FLOOR_S || raw > 2 * countdown + 2) secs = null;
    etaShown = { start, total, secs, at: now };
    return { kind: 'running', secs, raw };
}

/** Preparing bar: "7 of 12 · ~20 s". Time left = the larger of (a) the average time per finished file
 *  times the files left (files in flight count their progress) and (b) each file in flight's own pace
 *  (an upload's bytes per second say when it ends; PLAN C3). Usage QA 2026-10-07 (J2-10, J3-6, J15-5, J16-8):
 *  the number only goes down; it appears only with evidence and is withdrawn (never raised) when the work
 *  outlasts it — etaText then says "Still working…". `now` defaults to Date.now(). */
export function etaLabel(done: number, total: number, elapsedMs: number, active?: EtaActive[], now: number = Date.now()): string {
    const head = `${done} of ${total}`;
    const st = etaCompute(done, total, elapsedMs, active, now);
    if (st.secs == null) return head;
    const sec = Math.round(st.secs);
    return sec > 90 ? `${head} · ~${Math.round(sec / 60)} min` : `${head} · ~${Math.max(3, sec)} s`;
}

/** The preparing line's words (for the editor's prepLabel): "about 20 s left", "about 3 min left", "Starting…"
 *  before any estimate, "Still working…" once started without an honest number ("Almost done…" when the
 *  estimate itself says 3 s or less), "Finishing…" when every file is
 *  done. Same inputs and memo as etaLabel (call one of the two per tick). */
export function etaText(done: number, total: number, elapsedMs: number, active?: EtaActive[], now: number = Date.now()): string {
    const st = etaCompute(done, total, elapsedMs, active, now);
    if (st.kind === 'done') return 'Finishing…';
    if (st.secs == null) return st.kind === 'starting' ? 'Starting…' : st.raw != null && st.raw <= 3 ? 'Almost done…' : 'Still working…';
    const sec = Math.round(st.secs);
    return sec > 90 ? `about ${Math.round(sec / 60)} min left` : `about ${Math.max(3, sec)} s left`;
}

/** Runs tasks with at most `limit` in flight (per user: no global caps). One failure never stops
 *  the rest; once `cancelled()` turns true no new task starts. */
export async function runPool<T>(tasks: (() => Promise<T>)[], limit: number, cancelled: () => boolean = () => false): Promise<PromiseSettledResult<T>[]> {
    const out: PromiseSettledResult<T>[] = new Array(tasks.length);
    let next = 0;
    const lane = async () => {
        while (next < tasks.length && !cancelled()) {
            const i = next++;
            try { out[i] = { status: 'fulfilled', value: await tasks[i]() }; }
            catch (reason) { out[i] = { status: 'rejected', reason }; }
        }
    };
    await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, tasks.length)) }, lane));
    return out.filter(Boolean);
}

// ---------------------------------------------------------------- batch edits belong to the batch (fix round 1, BU-1/BU-2)
// QA 2026-10-05: each photo kept its own EditState and "the master" was whichever photo was selected,
// so clicking another thumbnail made an untouched photo the master and the ZIP lost every edit (LIVE
// on prod, flag off too). The batch owns ONE EditState (`batch`, drawn on the photo `ownerId` — the last
// photo edited without an own crop); what a photo shows AND exports is a pure function of
// (batch, ownerId, policy, that photo's size + own crop) — selecting a thumbnail never writes anything.
// Own crops (QA round 3 redesign, model/own-crop.ts): stored only on their photo, in its source pixels;
// that photo's pipeline = the batch's ops without crop steps + the own crop in the batch crop's place (or
// before the first resize, never before a turn or flip). `hasOwnCrop` tells an own crop that cuts nothing (review9
// batch-5) for the editor's tag and count.

/** What the batch needs to know about a photo. `size` = its decoded pixel size (null: not decoded here). */
export interface BatchMember { id: string; size: Size | null; ownCrop: OwnCrop | null; }

/** A size the editor does not know (the photo was never decoded here): null or 0 x 0. */
export function isUnsized(s: Size | null | undefined): boolean { return !s || s.width <= 0 || s.height <= 0; }

/** The batch as it lands on a photo, ignoring any own crop: verbatim on the photo it was drawn on,
 *  otherwise its translation (applyMasterToItem / unsizedStateFor). */
function batchOnMember(batch: EditState, ownerId: string | null, m: BatchMember, policy: CropPolicy): EditState {
    if (isUnsized(m.size)) {
        // the batch was drawn on this very photo (all-EPS/RAW batch): its ops ARE this photo's edits
        if (m.id === ownerId && isUnsized(batch.source)) return { ...batch, source: { width: 0, height: 0 } };
        return unsizedStateFor(batch, policy);
    }
    const size = m.size as Size;
    if (m.id === ownerId && size.width === batch.source.width && size.height === batch.source.height) return batch;
    return applyMasterToItem(batch, size, policy);
}

/** The state a photo shows (and, but for the `own` marker and server hints, exports). */
export function stateForMember(batch: EditState, ownerId: string | null, m: BatchMember, policy: CropPolicy = 'centre-aspect'): EditState {
    const base = batchOnMember(batch, ownerId, m, policy);
    // review9 batch-5 (an own crop that cuts nothing gives way to a later batch crop) needs the editor's badge,
    // count and scope radio to read `hasOwnCrop` in the same change, so the model keeps any own crop until then
    return m.ownCrop ? withOwnCrop(base, m.ownCrop) : base;
}

/**
 * What a photo is EXPORTED with (QA round 2 redesign: a single file is a batch of one). As
 * stateForMember without the view-only `own` marker, except for a photo whose size is unknown here (RAW
 * the header does not size): its crop boxes were drawn on a stand-in, so a crop with a chosen shape
 * travels as `centreAspect` (the edge centres that shape on the real size) — for one file as for fifty.
 */
export function exportStateForMember(batch: EditState, ownerId: string | null, m: BatchMember, policy: CropPolicy = 'centre-aspect'): EditState {
    const s = stateForMember(batch, ownerId, m, policy);
    const blind = isUnsized(m.size) && (isUnsized(batch.source) || !!m.ownCrop);
    return { ...s, ops: s.ops.map(op => {
        if (op.type !== 'crop') return op;
        const { own, ...plain } = op;
        return blind && plain.centreAspect == null && plain.aspect ? { ...plain, centreAspect: plain.aspect } : (own ? plain : op);
    }) };
}

const cropsOf = (s: EditState) => JSON.stringify(s.ops.filter(op => op.type === 'crop'));
const nonCropsOf = (s: EditState) => s.ops.filter(op => op.type !== 'crop');

/**
 * The batch with its non-crop ops `before` (as a photo with an own crop showed them, one-to-one with the
 * batch's non-crop ops: batchOnMember never adds or drops one) replaced by `after`; each batch crop keeps its
 * place among the ops that survive (review I-1). The two lists are aligned (edit distance: an equal op
 * matches, an op of the same tool may be changed in place, anything else is removed / added). A crop stays
 * right before the surviving op that followed it, or — when none follows — right after the surviving op
 * that preceded it. So a reset that REMOVES an op before a crop leaves the crop before the same later op
 * (not "after the same number of ops", which slid it past them: rotate, crop, flip -> flip, crop on the
 * other photos); undoing that reset puts the op back in front of the crop; and ops appended on the own
 * photo land after the crop (design point 4: append, never before).
 */
function reanchor(batch: EditState, before: Op[], after: Op[]): EditState {
    const n = before.length, m = after.length, BIG = 1e9;
    const same = (a: Op, b: Op) => a === b || JSON.stringify(a) === JSON.stringify(b);
    const sub = (a: Op, b: Op) => same(a, b) ? 0 : a.type === b.type ? 1 : BIG;
    const d: number[][] = Array.from({ length: n + 1 }, (_, i) => Array.from({ length: m + 1 }, (_, j) => i + j));
    for (let i = 1; i <= n; i++) for (let j = 1; j <= m; j++)
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + sub(before[i - 1], after[j - 1]));
    const to: number[] = new Array(n).fill(-1);   // before[i] -> its index in `after` (-1: removed)
    for (let i = n, j = m; i > 0 && j > 0;) {
        if (d[i][j] === d[i - 1][j - 1] + sub(before[i - 1], after[j - 1])) { to[i - 1] = j - 1; i--; j--; }
        else if (d[i][j] === d[i - 1][j] + 1) i--; else j--;
    }
    const at: Op[][] = Array.from({ length: m + 1 }, () => []);   // at[j]: crops placed right before after[j]
    let k = 0; let last = -1; let pending: Op[] = [];
    batch.ops.forEach(op => {
        if (op.type === 'crop') { pending.push(op); return; }
        if (k < n && to[k] >= 0) { at[to[k]].push(...pending); pending = []; last = to[k]; }
        k++;
    });
    at[last + 1].push(...pending);
    const out: Op[] = [];
    after.forEach((op, j) => { out.push(...at[j], op); });
    out.push(...at[m]);
    return { ...batch, ops: out };
}

/**
 * Fold an edit made on photo `m` (`edited` = its view after the edit) back into the model.
 * No own crop: an edit that changes m's crops re-draws the batch on m (the edited state IS the batch,
 * `edited.source` = m's size). One that changes only rotate / flip / resize goes into the batch with its
 * crops kept AS DRAWN on their photo (QA round 4 OC4-1: re-drawing them from m's view lost a crop that is
 * the full frame on m — 16:9 shown on a 1600 x 900 photo — from every other photo).
 * With an own crop: a change to the crop ops becomes m's new own crop (mapped back to source pixels);
 * a change to the other ops goes to the batch, whose crops keep their places. Nothing else is touched.
 */
export function foldEdit(batch: EditState | null, ownerId: string | null, m: BatchMember & { size: Size }, edited: EditState, policy: CropPolicy = 'centre-aspect'): { batch: EditState; ownerId: string; ownCrop: OwnCrop | null } {
    if (!m.ownCrop) {
        if (!batch || ownerId === m.id) return { batch: edited, ownerId: m.id, ownCrop: null };
        const shown = stateForMember(batch, ownerId, m, policy);
        if (cropsOf(shown) !== cropsOf(edited)) return { batch: edited, ownerId: m.id, ownCrop: null };
        return { batch: reanchor(batch, nonCropsOf(shown), nonCropsOf(edited)), ownerId: ownerId as string, ownCrop: null };
    }
    const b = batch ?? initialState(m.size);
    const prev = stateForMember(b, batch ? ownerId : m.id, m, policy);
    let ownCrop: OwnCrop = m.ownCrop;
    if (cropsOf(prev) !== cropsOf(edited)) {
        let j = edited.ops.length - 1; while (j >= 0 && edited.ops[j].type !== 'crop') j--;
        const view = { ...edited, source: workSize(m.size) };
        ownCrop = j < 0 ? fullOwnCrop(m.size) : ownCropFromView(view, j);
    }
    const next = nonCropsOf(edited);
    if (JSON.stringify(nonCropsOf(prev)) === JSON.stringify(next)) return { batch: b, ownerId: batch ? (ownerId as string) : m.id, ownCrop };
    return batch ? { batch: reanchor(b, nonCropsOf(prev), next), ownerId: ownerId as string, ownCrop } : { batch: { source: { ...m.size }, ops: next }, ownerId: m.id, ownCrop };
}

/**
 * The stage's Reset on photo `m` (QA round 4 OC4-2). A photo with its own crop drops ONLY that crop and shows
 * the batch again — the batch and every other photo are untouched ("Reset all" is the batch's reset). A photo
 * without one resets the batch (it IS the batch's view), as before.
 */
export function resetPhoto(batch: EditState | null, ownerId: string | null, m: BatchMember & { size: Size }, policy: CropPolicy = 'centre-aspect'): { batch: EditState | null; ownerId: string | null; ownCrop: null } {
    if (m.ownCrop) return { batch, ownerId, ownCrop: null };
    const view = batch ? stateForMember(batch, ownerId, m, policy) : initialState(m.size);
    const f = foldEdit(batch, ownerId, m, initialState(view.source), policy);
    return { batch: f.batch, ownerId: f.ownerId, ownCrop: null };
}

/**
 * "Only the selected photo": the crop photo `m` shows now becomes its own (in its source pixels). When m
 * is the photo the batch's crop was drawn on, that crop was meant for m: the rest of the batch loses it
 * (its crop steps are removed).
 */
export function takeOwnCrop(batch: EditState | null, ownerId: string | null, m: BatchMember, policy: CropPolicy = 'centre-aspect'): { batch: EditState | null; ownCrop: OwnCrop } {
    if (m.ownCrop) return { batch, ownCrop: m.ownCrop };
    const size = m.size ?? { width: 0, height: 0 };
    const view = batch ? stateForMember(batch, ownerId, m, policy) : initialState(workSize(size));
    let j = view.ops.length - 1; while (j >= 0 && view.ops[j].type !== 'crop') j--;
    const ownCrop = j < 0 ? fullOwnCrop(size) : ownCropFromView(view, j);
    const nextBatch = batch && ownerId === m.id ? { ...batch, ops: batch.ops.filter(op => op.type !== 'crop') } : batch;
    return { batch: nextBatch, ownCrop };
}

/**
 * A photo the browser cannot decode (EPS/RAW, server route): its size is unknown here, so a crop
 * cannot be re-fitted to it. Under 'centre-aspect' each crop carries `centreAspect` — the pixel W/H of
 * the batch's box at that step — and the edge centres a box of that shape on the file's real size
 * (SP-1). `f` keeps the batch's fractions for an edge that predates the field. 'same-region' travels as
 * fractions.
 */
export function unsizedStateFor(batch: EditState, policy: CropPolicy = 'centre-aspect'): EditState {
    const ops: Op[] = batch.ops.map((op, i) => {
        if (op.type !== 'crop' || (isIdentity(op) && !op.aspect)) return op;
        if (policy !== 'centre-aspect') return op;
        if (isUnsized(batch.source)) return op.aspect ? { ...op, centreAspect: op.aspect } : op;   // no boxes known: the chosen aspect, or the fractions
        const r = cropRectPx(op, sizeAfter(batch, i));
        return { ...op, centreAspect: r.width / Math.max(1, r.height) };
    });
    return { source: { width: 0, height: 0 }, ops };
}

// ---------------------------------------------------------------- output line + counts (BU-6, BU-7, SP-3)

export interface BatchCountItem { name: string; route: 'browser' | 'server'; failed: boolean; format: string; }
export interface BatchCounts { browser: number; server: number; serverFormats: string[]; ok: number; failed: number; failedNames: string[]; }

/** Route counts and the "N files" total count only the files that will be in the download; failed
 *  files are counted apart and named. */
export function batchCounts(items: BatchCountItem[]): BatchCounts {
    const ok = items.filter(i => !i.failed); const bad = items.filter(i => i.failed);
    const server = ok.filter(i => i.route === 'server');
    const serverFormats = server.map(i => i.format).filter((f, j, a) => a.indexOf(f) === j);
    return { browser: ok.length - server.length, server: server.length, serverFormats, ok: ok.length, failed: bad.length, failedNames: bad.map(i => i.name) };
}

/** A failure message that names the file ("bad12.jpg: The browser could not decode this image.").
 *  `code` (optional, PLAN C6; critic-5): a server 5xx names our server, not the user's connection; a 429
 *  keeps the server's wait time (`retryAfterSec`, else the server's own "Try again in …" message); any other
 *  or unknown code passes the message through. */
export function failureText(name: string, message: string, code?: string, retryAfterSec?: number): string {
    let msg = message;
    switch (code) {
        case 'server_error': msg = 'Our server had a problem with this file. Please try again.'; break;
        case 'rate_limited': {
            const limit = 'You\'ve reached the limit for now.';
            if (retryAfterSec && retryAfterSec > 0) {
                const m = Math.round(retryAfterSec / 60);
                msg = `${limit} Try again in ${retryAfterSec < 90 ? `${Math.round(retryAfterSec)} seconds` : `${m} minute${m === 1 ? '' : 's'}`}.`;
            } else if (!/try again in/i.test(message || '')) msg = `${limit} Please try again later.`;
            break;
        }
        default: break;
    }
    return name ? `${name}: ${msg}` : msg;
}
/** "Replace this file" helps only when the file itself is the problem — never for a limit or a server fault. */
export function failureOffersReplace(code?: string): boolean {
    return ['server_error', 'rate_limited', 'refused', 'upload', 'schedule', 'timeout', 'download', 'cancelled'].indexOf(code || '') < 0;
}
/** Transient failures where "Try again" can work. */
export function failureRetryable(code?: string): boolean {
    return ['server_error', 'upload', 'schedule', 'timeout', 'download'].indexOf(code || '') >= 0;
}

/**
 * The Output line's format + size for a batch: one label only when every file comes out in that
 * format, else "Mixed (JPG, TIFF, …)"; the byte estimate is the sum over files and is null when any
 * file's size cannot be estimated here (server files), never the selected photo's alone.
 */
export function batchOutputSummary(outputs: { format: string; bytes: number | null }[]): { label: string; bytes: number | null; mixed: boolean } {
    const fmts = outputs.map(o => o.format).filter((f, j, a) => a.indexOf(f) === j);
    const mixed = fmts.length > 1;
    const label = mixed ? `Mixed (${fmts.join(', ')})` : (fmts[0] ?? '');
    const bytes = outputs.length && outputs.every(o => o.bytes != null) ? outputs.reduce((a, o) => a + (o.bytes as number), 0) : null;
    return { label, bytes, mixed };
}
